import { convertCelsiusToFahrenheit } from 'shared/helpers/helpers';

export const STATUS = {
  FAULTS: 'faults',
  ACTIVE: 'active',
  READY: 'ready',
  OFF: 'off',
  STANDBY: 'standby',
};

export const STATUS_PRIORITY = {
  [STATUS.FAULTS]: 0,
  [STATUS.ACTIVE]: 1,
  [STATUS.READY]: 2,
  [STATUS.OFF]: 3,
  [STATUS.STANDBY]: 4,
};

export const STATUS_ACCENT = {
  [STATUS.FAULTS]: '#ef4444',
  [STATUS.ACTIVE]: '#00d492',
  [STATUS.READY]: '#3b82f6',
  [STATUS.OFF]: '#f97316',
  [STATUS.STANDBY]: '#475569',
};

export const STATUS_LABEL = {
  [STATUS.FAULTS]: 'Fault',
  [STATUS.ACTIVE]: 'Active',
  [STATUS.READY]: 'Ready',
  [STATUS.OFF]: 'Off',
  [STATUS.STANDBY]: 'Standby',
};

export const PROGRAM_META = {
  instantHeat: {
    label: 'Instant Heat',
    shortLabel: 'Heat',
    icon: '/images/logo-instantHeat.svg',
  },
  snowSensor: {
    label: 'Snow Sensor',
    shortLabel: 'Snow',
    icon: '/images/logo-snowSensor.svg',
  },
  optionalConstantTemp: {
    label: 'Optional Constant Temp',
    shortLabel: 'Const',
    icon: '/images/logo-constantTemp.svg',
  },
  heatingSchedule: {
    label: 'Heating Schedule',
    shortLabel: 'Sched',
    icon: '/images/logo-schedule.svg',
  },
  windFactor: {
    label: 'Wind Factor',
    shortLabel: 'Wind',
    icon: '/images/logo-windFactor.svg',
  },
  fanOnly: {
    label: 'Fan Only',
    shortLabel: 'Fan',
    icon: '/images/tgs-fanOnly.svg',
  },
};

const SYSTEM_PROGRAM_KEYS = {
  ess: ['instantHeat', 'snowSensor', 'optionalConstantTemp', 'heatingSchedule', 'windFactor'],
  tes: ['instantHeat', 'snowSensor', 'optionalConstantTemp', 'heatingSchedule', 'windFactor'],
  tgs: ['instantHeat', 'snowSensor', 'fanOnly', 'heatingSchedule', 'windFactor'],
};

export const getProgramKeysForSystem = (swtName) =>
  SYSTEM_PROGRAM_KEYS[swtName] || SYSTEM_PROGRAM_KEYS.ess;

export const getSwitchStatus = (machineData) => {
  if (!machineData) return STATUS.STANDBY;

  const {
    isFaults,
    isOff,
    instantHeat,
    snowSensor,
    windFactor,
    heatingSchedule,
    optionalConstantTemp,
    fanOnly,
  } = machineData;

  if (isFaults) return STATUS.FAULTS;
  if (isOff) return STATUS.OFF;

  const programs = [
    instantHeat,
    snowSensor,
    windFactor,
    heatingSchedule,
    optionalConstantTemp,
  ];

  if (programs.some((program) => program?.isActivated) || fanOnly) return STATUS.ACTIVE;
  if (programs.some((program) => program?.isReady)) return STATUS.READY;

  return STATUS.STANDBY;
};

export const isSwitchActive = (machineData) => {
  if (!machineData || machineData.isOff) return false;

  if (machineData.isWifi === true || machineData.deviceActive === true || machineData.isActive === true) {
    return true;
  }

  if (machineData.device_active === 1 || machineData.active === 1) {
    return true;
  }

  const deviceStatus = String(machineData.deviceStatus || machineData.device_status || '').toLowerCase();
  return deviceStatus === 'active' || deviceStatus === 'online';
};

export const getSwitchTypeLabel = (machineData) => {
  if (!machineData?.heatingSystem) return null;

  const heatingSystem = machineData.heatingSystem;

  if (heatingSystem.includes('Thermal')) return 'Thermal Switch';
  if (heatingSystem.includes('Environmental')) return 'Environmental';
  if (heatingSystem.includes('Heater')) return 'Heater Node';

  return heatingSystem;
};

export const isProgramActive = (machineData, key) => {
  if (!machineData) return false;

  switch (key) {
    case 'instantHeat':
      return machineData.instantHeat?.isActivated && machineData.machineMode === 'SWITCH';
    case 'snowSensor':
      return machineData.snowSensor?.isActivated && machineData.machineMode === 'SNOW';
    case 'optionalConstantTemp':
      return (
        machineData.optionalConstantTemp?.isActivated && machineData.machineMode === 'CONSTANT'
      );
    case 'heatingSchedule':
      return machineData.heatingSchedule?.isActivated && machineData.machineMode === 'SCHEDULE';
    case 'windFactor':
      return machineData.windFactor?.isActivated && machineData.machineMode === 'WIND';
    case 'fanOnly':
      return Boolean(machineData.fanOnly);
    default:
      return false;
  }
};

export const isProgramReady = (machineData, key) => {
  if (!machineData) return false;

  switch (key) {
    case 'instantHeat':
      return machineData.instantHeat?.isReady;
    case 'snowSensor':
      return machineData.snowSensor?.isReady;
    case 'optionalConstantTemp':
      return machineData.optionalConstantTemp?.isReady;
    case 'heatingSchedule':
      return machineData.heatingSchedule?.isReady;
    case 'windFactor':
      return machineData.windFactor?.isReady;
    default:
      return false;
  }
};

export const getProgramCount = (machineData, swtName) =>
  getProgramKeysForSystem(swtName).filter(
    (key) => isProgramActive(machineData, key) || isProgramReady(machineData, key)
  ).length;

export const getPrimaryProgramKey = (machineData, swtName) =>
  getProgramKeysForSystem(swtName).find((key) => isProgramActive(machineData, key)) ||
  getProgramKeysForSystem(swtName).find((key) => isProgramReady(machineData, key)) ||
  null;

export const sortMachineIds = (machineIds, machineMap) =>
  [...machineIds].sort((machineA, machineB) => {
    const statusA = getSwitchStatus(machineMap?.[machineA]);
    const statusB = getSwitchStatus(machineMap?.[machineB]);

    if (STATUS_PRIORITY[statusA] !== STATUS_PRIORITY[statusB]) {
      return STATUS_PRIORITY[statusA] - STATUS_PRIORITY[statusB];
    }

    const labelA = machineMap?.[machineA]?.machineName || machineA;
    const labelB = machineMap?.[machineB]?.machineName || machineB;

    return labelA.localeCompare(labelB);
  });

export const getMachineTemperatureDisplay = (machineData, isF) => {
  if (!machineData?.thermocouple) {
    return { value: '--', unit: isF ? 'F' : 'C' };
  }

  const baseTemp = Number.isFinite(machineData?.currentTemp)
    ? machineData.currentTemp
    : Number.isFinite(machineData?.enclosureTemp)
    ? machineData.enclosureTemp
    : null;

  if (!Number.isFinite(baseTemp)) {
    return { value: '--', unit: isF ? 'F' : 'C' };
  }

  return {
    value: Math.round(isF ? convertCelsiusToFahrenheit(baseTemp) : baseTemp),
    unit: isF ? 'F' : 'C',
  };
};

export const getSetTemp = (machineData, isF) => {
  if (!machineData?.thermocouple) return '--';

  const anyProgramEngaged =
    machineData.instantHeat?.isReady ||
    machineData.optionalConstantTemp?.isReady ||
    machineData.heatingSchedule?.isActivated ||
    (machineData.snowSensor?.isReady && machineData.snowSensor?.isActivated) ||
    (machineData.windFactor?.isReady && machineData.windFactor?.isActivated);

  if (!anyProgramEngaged) return '--';

  const raw = machineData.setTemp;
  if (!Number.isFinite(raw)) return '--';

  return Math.round(isF ? convertCelsiusToFahrenheit(raw) : raw);
};

export const getProgramStatuses = (machineData, swtName) =>
  getProgramKeysForSystem(swtName).map((key) => ({
    key,
    meta: PROGRAM_META[key],
    isActive: isProgramActive(machineData, key),
    isReady: isProgramReady(machineData, key),
  }));

export const getEbpLocked = (machineData) => {
  return machineData?.EBP_mode === 0 && Boolean(machineData?.isEbp);
};

export const getZoneProgramSummary = (machineMap, swtName) => {
  const machines = Object.values(machineMap || {});

  return getProgramKeysForSystem(swtName).map((key) => {
    const activeCount = machines.filter((machine) => isProgramActive(machine, key)).length;
    const readyCount = machines.filter(
      (machine) => !isProgramActive(machine, key) && isProgramReady(machine, key)
    ).length;
    const engagedCount = activeCount + readyCount;
    const faultCount = machines.filter(
      (machine) => machine?.isFaults && (isProgramActive(machine, key) || isProgramReady(machine, key))
    ).length;

    return {
      key,
      meta: PROGRAM_META[key],
      activeCount,
      readyCount,
      engagedCount,
      faultCount,
    };
  });
};
